import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useSpring, animated } from "react-spring";
import { BREAKPOINTS } from "../../../helpers/theme";
import { toVWDesktop } from "../../../helpers/styles";
import * as CommonHeaderTypes from "../types";
import DrawerSubMenuItem from "./DrawerSubMenuItem";
import DrawerSubMenuItemView from "./DrawerSubMenuItemView";

const ROOT_ELEMENT_ID = {
  LARGE: "menu-desktop-large-drawer",
  SMALL: "menu-desktop-small-drawer",
};

export type DrawerProps = {
  className?: string;
  open: boolean;
  onClose: () => void;
  selectedMenuItem: CommonHeaderTypes.MenuItem | undefined;
};

const Drawer = ({
  className,
  selectedMenuItem,
  open,
  onClose,
}: DrawerProps) => {
  const styles = useSpring({ opacity: open ? 1 : 0 });

  const subMenuItems: Array<CommonHeaderTypes.SubMenuItem> = useMemo(() => {
    return selectedMenuItem?.subMenuItems || [];
  }, [selectedMenuItem]);

  const [selectedSubMenuItem, setSelectedSubMenuItem] =
    useState<CommonHeaderTypes.SubMenuItem>();

  const shouldRenderFullView = useCallback(() => {
    if (!subMenuItems) {
      return false;
    }
    return (
      subMenuItems.filter((subMenuItem) => subMenuItem?.options?.length > 0)
        .length > 0
    );
  }, [subMenuItems]);

  const getDrawerPosition = useCallback(() => {
    try {
      const elements = document.querySelectorAll(".menu-item");
      const element = (Array.from(elements) || []).filter(
        (e) =>
          selectedMenuItem && e?.textContent?.includes(selectedMenuItem.label)
      )[0];
      return element.getBoundingClientRect();
    } catch (e) {
      return null;
    }
  }, [selectedMenuItem]);

  useEffect(() => {
    if (shouldRenderFullView()) {
      setSelectedSubMenuItem(subMenuItems[0]);
    }
  }, [subMenuItems, shouldRenderFullView]);

  const getTopMenuItems = useCallback(() => {
    return Array.from(
      document.querySelectorAll<HTMLElement>("#theNavBar .menu-item"),
    );
  }, []);

  const focusCurrentTopMenuItem = useCallback(() => {
    const normalizedSelectedLabel = selectedMenuItem?.label
      ?.toLowerCase()
      ?.trim();
    const topMenuItems = getTopMenuItems();
    const currentTopMenuItem = topMenuItems.find((element) => {
      const normalizedElementLabel = element?.textContent
        ?.toLowerCase()
        ?.trim();
      return (
        normalizedSelectedLabel &&
        normalizedElementLabel === normalizedSelectedLabel
      );
    });

    currentTopMenuItem?.focus();
  }, [getTopMenuItems, selectedMenuItem]);

  const focusNextTopMenuItem = useCallback(() => {
    const normalizedSelectedLabel = selectedMenuItem?.label
      ?.toLowerCase()
      ?.trim();
    const topMenuItems = getTopMenuItems();
    const currentTopMenuItemIndex = topMenuItems.findIndex((element) => {
      const normalizedElementLabel = element?.textContent
        ?.toLowerCase()
        ?.trim();
      return (
        normalizedSelectedLabel &&
        normalizedElementLabel === normalizedSelectedLabel
      );
    });

    if (
      currentTopMenuItemIndex > -1 &&
      currentTopMenuItemIndex < topMenuItems.length - 1
    ) {
      topMenuItems[currentTopMenuItemIndex + 1]?.focus();
      return;
    }

    const firstSecondaryAction = document.querySelector<HTMLElement>(
      "#theNavBar .secondary-actions button",
    );
    onClose();
    window.requestAnimationFrame(() => {
      firstSecondaryAction?.focus();
    });
  }, [getTopMenuItems, onClose, selectedMenuItem]);

  const handleDrawerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const drawerElement = event.currentTarget;
      const submenuTriggerElements = Array.from(
        drawerElement.querySelectorAll<HTMLElement>(
          '[data-drawer-submenu-trigger="true"]',
        ),
      );
      const leftPanelFocusableElements = Array.from(
        drawerElement.querySelectorAll<HTMLElement>(
          '[data-drawer-left-panel="true"] a[href], [data-drawer-left-panel="true"] button',
        ),
      ).filter((element) => !element.hasAttribute("disabled"));
      const rightPanelFocusableElements = Array.from(
        drawerElement.querySelectorAll<HTMLElement>(
          '[data-drawer-submenu-content] a[href], [data-drawer-submenu-content] button',
        ),
      ).filter((element) => !element.hasAttribute("disabled"));
      const focusableElements = Array.from(
        drawerElement.querySelectorAll<HTMLElement>("a[href], button"),
      ).filter((element) => !element.hasAttribute("disabled"));

      if (focusableElements.length === 0) {
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];
      const targetElement = event.target as HTMLElement;
      const activeSubmenuTriggerElement = submenuTriggerElements.find(
        (element) => element.getAttribute("aria-expanded") === "true",
      );
      const isTargetSubmenuTrigger =
        submenuTriggerElements.includes(targetElement);
      const isTargetLeftPanelFocusable =
        leftPanelFocusableElements.includes(targetElement);
      const isTargetRightPanelFocusable =
        rightPanelFocusableElements.includes(targetElement);
      const firstRightPanelFocusableElement = rightPanelFocusableElements[0];
      const lastRightPanelFocusableElement =
        rightPanelFocusableElements[rightPanelFocusableElements.length - 1];
      const focusElementByOffset = (
        elements: HTMLElement[],
        currentElement: HTMLElement,
        offset: number,
      ) => {
        const currentIndex = elements.indexOf(currentElement);

        if (currentIndex === -1 || elements.length === 0) {
          return;
        }

        const nextIndex =
          (currentIndex + offset + elements.length) % elements.length;
        elements[nextIndex]?.focus();
      };

      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        window.requestAnimationFrame(() => {
          focusCurrentTopMenuItem();
        });
        return;
      }

      if (event.key === "ArrowDown") {
        if (isTargetLeftPanelFocusable) {
          event.preventDefault();
          focusElementByOffset(leftPanelFocusableElements, targetElement, 1);
          return;
        }

        if (isTargetRightPanelFocusable) {
          event.preventDefault();
          focusElementByOffset(rightPanelFocusableElements, targetElement, 1);
        }
        return;
      }

      if (event.key === "ArrowUp") {
        if (isTargetLeftPanelFocusable) {
          event.preventDefault();
          focusElementByOffset(leftPanelFocusableElements, targetElement, -1);
          return;
        }

        if (isTargetRightPanelFocusable) {
          event.preventDefault();
          focusElementByOffset(rightPanelFocusableElements, targetElement, -1);
        }
        return;
      }

      if (event.key === "ArrowRight") {
        if (
          isTargetLeftPanelFocusable &&
          isTargetSubmenuTrigger &&
          rightPanelFocusableElements.length > 0
        ) {
          event.preventDefault();
          firstRightPanelFocusableElement?.focus();
        }
        return;
      }

      if (event.key === "ArrowLeft") {
        if (isTargetRightPanelFocusable) {
          event.preventDefault();
          activeSubmenuTriggerElement?.focus();
          return;
        }

        if (isTargetLeftPanelFocusable) {
          event.preventDefault();
          focusCurrentTopMenuItem();
        }
        return;
      }

      if (event.key === "Home") {
        if (isTargetLeftPanelFocusable) {
          event.preventDefault();
          leftPanelFocusableElements[0]?.focus();
          return;
        }

        if (isTargetRightPanelFocusable) {
          event.preventDefault();
          firstRightPanelFocusableElement?.focus();
        }
        return;
      }

      if (event.key === "End") {
        if (isTargetLeftPanelFocusable) {
          event.preventDefault();
          leftPanelFocusableElements[
            leftPanelFocusableElements.length - 1
          ]?.focus();
          return;
        }

        if (isTargetRightPanelFocusable) {
          event.preventDefault();
          lastRightPanelFocusableElement?.focus();
        }
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      if (event.shiftKey && targetElement === firstFocusableElement) {
        event.preventDefault();
        focusCurrentTopMenuItem();
        return;
      }

      if (
        !event.shiftKey &&
        isTargetSubmenuTrigger &&
        rightPanelFocusableElements.length > 0
      ) {
        event.preventDefault();
        window.requestAnimationFrame(() => {
          const refreshedRightPanelFirstFocusableElement =
            drawerElement.querySelector<HTMLElement>(
              '[data-drawer-submenu-content] a[href], [data-drawer-submenu-content] button',
            );
          refreshedRightPanelFirstFocusableElement?.focus();
        });
        return;
      }

      if (
        event.shiftKey &&
        firstRightPanelFocusableElement &&
        targetElement === firstRightPanelFocusableElement
      ) {
        event.preventDefault();
        activeSubmenuTriggerElement?.focus();
        return;
      }

      if (
        !event.shiftKey &&
        lastRightPanelFocusableElement &&
        targetElement === lastRightPanelFocusableElement
      ) {
        const activeLeftPanelElementIndex = leftPanelFocusableElements.findIndex(
          (element) => element === activeSubmenuTriggerElement,
        );
        const nextLeftPanelFocusableElement =
          leftPanelFocusableElements[activeLeftPanelElementIndex + 1];

        if (
          activeLeftPanelElementIndex > -1 &&
          nextLeftPanelFocusableElement
        ) {
          event.preventDefault();
          nextLeftPanelFocusableElement.focus();
          return;
        }
      }

      if (!event.shiftKey && targetElement === lastFocusableElement) {
        event.preventDefault();
        focusNextTopMenuItem();
      }
    },
    [focusCurrentTopMenuItem, focusNextTopMenuItem, onClose],
  );

  if (!subMenuItems || subMenuItems.length === 0) {
    return null;
  }

  if (!shouldRenderFullView()) {
    const drawerPosition = getDrawerPosition();
    return (
      <SmallContainer
        id={ROOT_ELEMENT_ID.SMALL}
        className={className}
        style={styles}
        left={`calc(${drawerPosition?.left}px - ${toVWDesktop(40)}vw)`}
        data-drawer-left-panel="true"
        onKeyDown={handleDrawerKeyDown}
      >
        {subMenuItems.map((item, i) => (
          <>
            <StyledDrawerSubMenuItemView
              subMenuItem={item}
              active={selectedSubMenuItem?.label === item.label}
              noMarginTop={i === 0}
              onClick={(subMenuItem) => setSelectedSubMenuItem(subMenuItem)}
            />
          </>
        ))}
      </SmallContainer>
    );
  }

  return (
    <Container
      id={ROOT_ELEMENT_ID.LARGE}
      className={className}
      style={styles}
      onKeyDown={handleDrawerKeyDown}
    >
      <LeftContent data-drawer-left-panel="true">
        <div>
          {subMenuItems.map((item, i) => (
            <>
              <StyledDrawerSubMenuItemView
                subMenuItem={item}
                active={selectedSubMenuItem?.label === item.label}
                noMarginTop={i === 0}
                onClick={(subMenuItem) => setSelectedSubMenuItem(subMenuItem)}
              />
            </>
          ))}
        </div>
      </LeftContent>
      <RightContent data-drawer-submenu-content="true">
        <DrawerSubMenuItemView
          subMenuItem={
            selectedSubMenuItem as CommonHeaderTypes.SubMenuItemWithOptions
          }
        />
      </RightContent>
    </Container>
  );
};

const Container = styled(animated.div)`
  display: none;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: flex;
    position: absolute;
    width: 100%;
  }
`;

const SmallContainer = styled(animated.div)<{ left: string }>`
  padding: ${toVWDesktop(40)}vw;
  background-color: var(--color-white);
  left: ${(props) => props.left};
  display: flex;
  flex-direction: column;
`;

const LeftContent = styled.div`
  width: 37%;
  background-color: var(--color-white);
  display: flex;
  justify-content: flex-end;
  padding: ${toVWDesktop(38)}vw ${toVWDesktop(65)}vw;
  & > div {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: ${(props) =>
      props.theme.isRTL
        ? `0 0 0 ${toVWDesktop(35)}vw`
        : `0 ${toVWDesktop(35)}vw 0 0`};
  }
`;

const RightContent = styled.div`
  width: 63%;
  background-color: var(--color-green-100);
  padding: ${toVWDesktop(38)}vw ${toVWDesktop(65)}vw;
`;

const StyledDrawerSubMenuItemView = styled(DrawerSubMenuItem)<{
  noMarginTop?: boolean;
}>`
  margin-top: ${(props) => toVWDesktop(props.noMarginTop ? 0 : 7)}vw;
`;

export default Drawer;
